import "./Skeleton.css";

const BASE = "animate-pulse motion-reduce:animate-none rounded-lg bg-[#DCE8F3]";

function SkeletonBlock({ className = "" }) {
	return <div aria-hidden="true" className={`${BASE} ${className}`} />;
}

function SkeletonCard({ type = "workshop", featured = false, className = "" }) {
	if (featured && type === "webinar") {
		return (
			<div
				aria-hidden="true"
				className={`relative h-[400px] overflow-hidden rounded-[20px] bg-[#DCE8F3] md:h-[480px] ${className}`}
			>
				<div className="absolute inset-x-5 bottom-5 space-y-3">
					<SkeletonBlock className="h-5 w-4/5 bg-white/60" />
					<SkeletonBlock className="h-5 w-3/5 bg-white/60" />
					<div className="flex items-center gap-2 pt-1">
						<SkeletonBlock className="h-10 w-10 shrink-0 rounded-full bg-white/60" />
						<SkeletonBlock className="h-4 w-1/3 bg-white/60" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			aria-hidden="true"
			className={`flex h-full min-w-0 flex-col rounded-[14px] border border-darkBlue/10 bg-white p-[10px] shadow-[0_4px_16px_rgba(39,106,166,0.06)] ${className}`}
		>
			<SkeletonBlock className="aspect-[4/3] w-full rounded-[10px]" />
			<SkeletonBlock className="mt-3 h-5 w-4/5" />
			<SkeletonBlock className="mt-2 h-5 w-3/5" />
			<div className="mt-3 flex min-h-[40px] items-center gap-2">
				<SkeletonBlock className="h-10 w-10 shrink-0 rounded-full" />
				<SkeletonBlock className="h-4 w-2/5" />
			</div>
			<SkeletonBlock className="mt-auto h-10 w-full rounded-[10px]" />
		</div>
	);
}

/**
 * Reusable loading placeholder for home content and catalog pages.
 * Variants: banner, media, detail, card, grid, carousel, and section.
 * Types: workshop, videoclip, and webinar (webinar uses its larger feature card).
 */
export default function Skeleton({
	variant = "card",
	type = "workshop",
	count = 3,
	className = "",
	showTitle = true,
}) {
	const safeCount = Math.max(1, Number(count) || 1);
	const label = "در حال بارگذاری محتوا";

	if (variant === "banner") {
		return (
			<div role="status" aria-label={label} className={className}>
				<SkeletonBlock className="aspect-[3/1] w-full rounded-xl sm:aspect-[1.35/1]" />
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	if (variant === "media") {
		return (
			<div role="status" aria-label={label} className={className}>
				<SkeletonBlock className="aspect-video w-full rounded-[12px]" />
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	if (variant === "detail") {
		return (
			<div role="status" aria-label={label} className={`grid gap-5 md:grid-cols-[minmax(0,1fr)_300px] ${className}`}>
				<div className="space-y-5">
					<SkeletonBlock className="h-64 w-full rounded-2xl" />
					<SkeletonBlock className="h-7 w-3/4" />
					<SkeletonBlock className="h-5 w-1/2" />
					<SkeletonBlock className="h-40 w-full rounded-2xl" />
				</div>
				<div className="hidden space-y-4 md:block">
					<SkeletonBlock className="h-48 w-full rounded-2xl" />
					<SkeletonBlock className="h-32 w-full rounded-2xl" />
				</div>
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	if (variant === "grid") {
		return (
			<div role="status" aria-label={label} className={className}>
				{showTitle && <SkeletonBlock className="mb-4 h-6 w-44" />}
				<div className="skeleton-grid gap-5">
					{Array.from({ length: safeCount }, (_, index) => (
						<SkeletonCard key={index} type={type} />
					))}
				</div>
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	if (variant === "carousel" || variant === "section") {
		const isWebinar = type === "webinar";
		return (
			<div role="status" aria-label={label} className={className}>
				{showTitle && <SkeletonBlock className="mb-4 h-6 w-44" />}
				<div className="flex gap-3 overflow-hidden">
					{Array.from({ length: safeCount }, (_, index) => (
						<SkeletonCard
							key={index}
							type={type}
							featured={variant === "carousel"}
							className={[
								"w-[86%] shrink-0",
								isWebinar ? "md:w-[calc((100%-1.5rem)/3)]" : "sm:w-[calc((100%-0.75rem)/2)] md:w-[calc((100%-1.5rem)/3)]",
							].join(" ")}
						/>
					))}
				</div>
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	if (variant === "card") {
		return (
			<div role="status" aria-label={label} className={className}>
				<SkeletonCard type={type} />
				<span className="sr-only">{label}</span>
			</div>
		);
	}

	return null;
}
